import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Phone, Mail, Bell, BellOff, LogOut, CheckCircle2, Circle, RefreshCw, Trash2, MessageSquare } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import { AdminStoreTab, AdminGalleryTab } from "../components/AdminStoreTabs";
import AdminBlogTab from "../components/AdminBlogTab";
import AdminMenuTab from "../components/AdminMenuTab";

// Short two-tone chime (Web Audio) — no asset file needed
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tones = [
      { f: 880, t: 0.0 },
      { f: 1320, t: 0.18 },
    ];
    tones.forEach(({ f, t }) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, now + t);
      g.gain.exponentialRampToValueAtTime(0.18, now + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.35);
      o.connect(g).connect(ctx.destination);
      o.start(now + t);
      o.stop(now + t + 0.4);
    });
    setTimeout(() => ctx.close().catch(() => {}), 900);
  } catch (_e) { /* audio blocked — silent */ }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function fmt(dt) {
  if (!dt) return "";
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

function HolidayLeadDetails({ lead }) {
  return (
    <div className="mt-3 space-y-3" data-testid={`lead-${lead.id}-holiday`}>
      <p className="font-serif text-sm text-ink/80">Pickup: <b>{lead.pickup_date}</b>{lead.paypal_order_id ? ` · PayPal #${lead.paypal_order_id}` : ""}</p>
      <div className="border border-ink/30">
        {lead.items.map((it, i) => (
          <div key={i} className="flex justify-between gap-2 border-b border-ink/10 px-3 py-2 font-serif text-sm">
            <span>{it.qty}× {it.name}</span><span className="font-mono">${(it.price * it.qty).toFixed(2)}</span>
          </div>
        ))}
        <div className="flex justify-between px-3 py-1 font-mono text-xs text-ink/70"><span>Subtotal ${lead.subtotal.toFixed(2)} · Tax ${lead.tax.toFixed(2)}</span></div>
        <div className="flex items-center justify-between border-t-2 border-ink bg-ink px-3 py-3 font-alfa text-lg uppercase text-bone">
          <span>Total</span><span>${lead.total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

function LeadCard({ lead, onToggle, onDelete, selected, onSelectToggle, isNew }) {
  const isCatering = lead.kind === "catering";
  const isEvent = lead.kind === "event";
  const isHoliday = lead.kind === "holiday";
  const phone = lead.phone;
  const email = lead.email;
  const dial = phone ? `tel:${phone.replace(/[^0-9+]/g, "")}` : null;
  const mailto = email ? `mailto:${email}` : null;
  const displayName = isEvent ? `${lead.first_name || ""} ${lead.last_name || ""}`.trim() : lead.name;

  const printLead = () => {
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return;
    const rows = (isEvent ? lead.entries || [] : []).map(
      (e) => `<tr><td>${e.name}</td><td style="text-align:center">${e.qty}</td><td style="text-align:right">$${e.unit_price.toFixed(2)}</td><td style="text-align:right">$${(e.unit_price * e.qty).toFixed(2)}</td></tr>`
    ).join("");
    w.document.write(`<!doctype html><html><head><title>Lead — ${displayName}</title>
      <style>body{font-family:Georgia,serif;padding:32px;color:#111}h1{border-bottom:2px solid #111;text-transform:uppercase}
      table{width:100%;border-collapse:collapse;margin-top:12px}td,th{padding:8px;border-bottom:1px solid #ccc;text-align:left}
      th{background:#111;color:#fff}dt{font-weight:bold;text-transform:uppercase;font-size:11px;letter-spacing:.1em;margin-top:12px}
      dd{margin:2px 0 8px 0}</style></head><body>
      <h1>${isHoliday ? "Holiday Order" : isEvent ? "Event Registration" : isCatering ? "Catering Lead" : "Contact Lead"}</h1>
      <p><b>${displayName}</b> — ${fmt(lead.created_at)}</p>
      <dl>
        ${email ? `<dt>Email</dt><dd>${email}</dd>` : ""}
        ${phone ? `<dt>Phone</dt><dd>${phone}</dd>` : ""}
        ${isEvent && lead.team_name ? `<dt>Team</dt><dd>${lead.team_name}</dd>` : ""}
        ${isEvent && lead.trailer_size ? `<dt>Trailer / Space</dt><dd>${lead.trailer_size}</dd>` : ""}
        ${isEvent ? `<dt>Address</dt><dd>${lead.street || ""}${lead.street2 ? ", " + lead.street2 : ""}, ${lead.city || ""}, ${lead.state || ""} ${lead.zip_code || ""}</dd>` : ""}
        ${isHoliday ? `<dt>Pickup</dt><dd>${lead.pickup_date} · ${lead.status} · $${(lead.total || 0).toFixed(2)}</dd><dt>Items</dt><dd>${(lead.items || []).map((it) => `${it.qty}× ${it.name}`).join("<br>")}</dd>` : ""}
        ${isCatering ? `<dt>Event</dt><dd>${lead.event_type} · ${lead.guest_count} guests · ${lead.event_date} @ ${lead.event_location}</dd>` : ""}
        ${lead.subject ? `<dt>Subject</dt><dd>${lead.subject}</dd>` : ""}
        ${lead.message || lead.additional_details || lead.notes ? `<dt>Details</dt><dd>${(lead.message || lead.additional_details || lead.notes || "").replace(/\n/g, "<br>")}</dd>` : ""}
      </dl>
      ${isEvent ? `<h2>Contest Entries</h2><table><thead><tr><th>Entry</th><th>Qty</th><th>Unit</th><th>Subtotal</th></tr></thead><tbody>${rows}</tbody><tfoot><tr><td colspan="3" style="text-align:right;font-weight:bold">TOTAL</td><td style="text-align:right;font-weight:bold">$${(lead.total || 0).toFixed(2)}</td></tr></tfoot></table>` : ""}
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div
      data-testid={`lead-${lead.id}`}
      data-new={isNew ? "true" : undefined}
      className={`border p-5 md:p-6 ${selected ? "border-ink bg-white ring-2 ring-ink" : "border-ink"} ${lead.handled ? "bg-bone/50 opacity-70" : "bg-white"} ${isNew ? "lead-pulse" : ""}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <label className="inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => onSelectToggle(lead.id)}
              data-testid={`lead-${lead.id}-select`}
              className="h-4 w-4 cursor-pointer border-ink accent-ink"
            />
          </label>
          <span className={`inline-block border border-ink px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${isEvent || isHoliday ? "bg-red-700 text-bone" : isCatering ? "bg-ink text-bone" : "bg-white text-ink"}`}>
            {isHoliday ? "Holiday Order" : isEvent ? "Event" : isCatering ? "Catering" : "Contact"}
          </span>
          {isHoliday && (
            <span data-testid={`lead-${lead.id}-pay-status`} className={`inline-block border-2 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] ${lead.status === "Paid" ? "border-green-700 bg-green-700 text-bone" : "border-amber-600 text-amber-700"}`}>
              {lead.status === "Paid" ? `Paid $${(lead.total || 0).toFixed(2)}` : "Unpaid"}
            </span>
          )}
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{fmt(lead.created_at)}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={`lead-${lead.id}-print`}
            onClick={printLead}
            aria-label="Print / save lead"
            className="inline-flex items-center gap-2 border border-ink px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-bone"
          >
            Print
          </button>
          <button
            data-testid={`lead-${lead.id}-toggle-handled`}
            onClick={() => onToggle(lead.id, !lead.handled)}
            className="inline-flex items-center gap-2 border border-ink px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-bone"
          >
            {lead.handled ? <CheckCircle2 size={14} /> : <Circle size={14} />}
            {lead.handled ? "Handled" : "Mark handled"}
          </button>
          <button
            data-testid={`lead-${lead.id}-delete`}
            onClick={() => onDelete(lead.id, displayName)}
            aria-label="Delete lead"
            className="inline-flex items-center gap-2 border border-ink px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-colors hover:border-red-700 hover:bg-red-700 hover:text-bone"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <h3 className="mt-4 font-alfa text-2xl uppercase leading-tight">{displayName}</h3>
      {isCatering && (
        <p className="mt-1 font-serif text-sm text-ink/80">
          {lead.event_type} · {lead.guest_count} guests · {lead.event_date} {lead.event_time ? `at ${lead.event_time}` : ""} · {lead.event_location}
        </p>
      )}
      {isEvent && (
        <div className="mt-1 space-y-1 font-serif text-sm text-ink/80">
          {lead.team_name && <p>Team: <b>{lead.team_name}</b></p>}
          {lead.trailer_size && <p>Trailer / Space: <b>{lead.trailer_size}</b></p>}
          <p>{lead.street}{lead.street2 ? `, ${lead.street2}` : ""} · {lead.city}, {lead.state} {lead.zip_code}</p>
        </div>
      )}
      {!isCatering && !isEvent && lead.subject && (
        <p className="mt-1 font-serif text-sm italic text-ink/70">{lead.subject}</p>
      )}

      {isHoliday && <HolidayLeadDetails lead={lead} />}

      {/* Event: contest entries table */}
      {isEvent && lead.entries && lead.entries.length > 0 && (
        <div className="mt-4 border border-ink/30">
          <div className="grid grid-cols-[1fr,60px,80px,90px] items-center gap-2 border-b border-ink/30 bg-bone px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">
            <span>Entry</span>
            <span className="text-center">Qty</span>
            <span className="text-right">Unit</span>
            <span className="text-right">Subtotal</span>
          </div>
          {lead.entries.map((e, i) => (
            <div key={i} className="grid grid-cols-[1fr,60px,80px,90px] items-center gap-2 border-b border-ink/10 px-3 py-2 font-serif text-sm last:border-b-0">
              <span>{e.name}</span>
              <span className="text-center font-mono">{e.qty}</span>
              <span className="text-right font-mono">${e.unit_price.toFixed(2)}</span>
              <span className="text-right font-mono">${(e.unit_price * e.qty).toFixed(2)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between border-t-2 border-ink bg-ink px-3 py-3 font-alfa text-lg uppercase text-bone">
            <span>Total</span>
            <span>${(lead.total || 0).toFixed(2)}</span>
          </div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {dial && (
          <a
            href={dial}
            data-testid={`lead-${lead.id}-call`}
            className="inline-flex items-center justify-center gap-2 border-2 border-ink bg-ink px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-bone transition-colors hover:bg-bone hover:text-ink"
          >
            <Phone size={14} /> Call {phone}
          </a>
        )}
        {isHoliday && dial && (
          <a
            href={`sms:${phone.replace(/[^0-9+]/g, "")}?&body=${encodeURIComponent(`Hi ${displayName}, this is When Pigs Fly Family BBQ confirming your holiday order for pickup ${lead.pickup_date}. Reply or call us with any questions!`)}`}
            data-testid={`lead-${lead.id}-text`}
            className="inline-flex items-center justify-center gap-2 border-2 border-ink px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-bone"
          >
            <MessageSquare size={14} /> Text to confirm
          </a>
        )}
        {mailto && (
          <a
            href={isHoliday ? `${mailto}?subject=${encodeURIComponent("Your When Pigs Fly holiday order")}&body=${encodeURIComponent(`Hi ${displayName},\n\nThanks for your holiday order! Confirming pickup on ${lead.pickup_date}:\n${(lead.items || []).map((it) => `${it.qty}x ${it.name}`).join("\n")}\n\nTotal: $${(lead.total || 0).toFixed(2)} (${lead.status})\n\nQuestions? Call (727) 427-5327.\n\nWhen Pigs Fly Family BBQ`)}` : mailto}
            data-testid={`lead-${lead.id}-email`}
            className="inline-flex items-center justify-center gap-2 border-2 border-ink px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-bone"
          >
            <Mail size={14} /> Email
          </a>
        )}
      </div>

      {(lead.message || lead.additional_details || lead.menu_preferences || lead.notes) && (
        <div className="mt-5 border-t border-ink/20 pt-4 font-serif text-sm leading-relaxed text-ink/85 whitespace-pre-wrap">
          {lead.message || lead.menu_preferences || lead.notes || ""}
          {lead.additional_details && (
            <div className="mt-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">Additional details</div>
              <div>{lead.additional_details}</div>
            </div>
          )}
        </div>
      )}

      {lead.best_time_to_reach && (
        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
          Best time to reach: <span className="text-ink/80">{lead.best_time_to_reach}</span>
        </div>
      )}
    </div>
  );
}

function LoginForm({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      toast.success("Welcome back.");
      onLoggedIn();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-6 py-16">
      <div className="eyebrow mb-3 text-ink/70">Owner Access</div>
      <h1 className="font-rye text-5xl uppercase leading-[0.9]">The Back Office</h1>
      <p className="mt-4 font-serif text-sm text-ink/70">
        Sign in to see catering & contact leads. Owner only.
      </p>
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="owner-login-form">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink/70">Email</span>
          <input
            type="email"
            value={email}
            required
            onChange={(e) => setEmail(e.target.value)}
            data-testid="owner-login-email"
            className="mt-2 w-full border border-ink bg-white px-3 py-3 font-mono text-sm focus:outline-none"
            autoComplete="username"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink/70">Password</span>
          <input
            type="password"
            value={password}
            required
            onChange={(e) => setPassword(e.target.value)}
            data-testid="owner-login-password"
            className="mt-2 w-full border border-ink bg-white px-3 py-3 font-mono text-sm focus:outline-none"
            autoComplete="current-password"
          />
        </label>
        <button
          type="submit"
          disabled={loading}
          data-testid="owner-login-submit"
          className="w-full border-2 border-ink bg-ink px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-bone transition-colors hover:bg-white hover:text-ink disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ owner, onLogout }) {
  const [section, setSection] = useState("leads"); // leads | store | gallery
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [pushOn, setPushOn] = useState(false);
  const [newIds, setNewIds] = useState(new Set());
  const knownIdsRef = useRef(null); // null until first successful load
  const baseTitleRef = useRef(typeof document !== "undefined" ? document.title : "");

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/owner/leads");
      const list = Array.isArray(data) ? data : [];

      // Detect newly-arrived leads compared to what we already saw
      const known = knownIdsRef.current;
      if (known !== null) {
        const fresh = list.filter((l) => !known.has(l.id));
        if (fresh.length > 0) {
          playChime();
          fresh.forEach((l) => {
            const label = l.kind === "holiday"
              ? `New Holiday Order · ${l.status}`
              : l.kind === "catering"
              ? `New Catering Lead · ${l.guest_count || "?"} guests`
              : "New Contact Lead";
            toast.success(`🔔 ${label} — ${l.name}`, { duration: 8000 });
          });
          setNewIds((prev) => {
            const n = new Set(prev);
            fresh.forEach((l) => n.add(l.id));
            // Auto-clear pulse after 6s
            setTimeout(() => {
              setNewIds((cur) => {
                const c = new Set(cur);
                fresh.forEach((l) => c.delete(l.id));
                return c;
              });
            }, 6000);
            return n;
          });
        }
      }
      knownIdsRef.current = new Set(list.map((l) => l.id));
      setLeads(list);
    } catch {
      if (!silent) toast.error("Couldn't load leads");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll every 20s while the tab is open so new leads pop in even without a push
  useEffect(() => {
    const id = setInterval(() => load({ silent: true }), 20000);
    return () => clearInterval(id);
  }, [load]);

  // When tab gains focus, refresh immediately and clear the tab-title badge
  useEffect(() => {
    const onFocus = () => load({ silent: true });
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [load]);

  // Check current subscription state
  useEffect(() => {
    (async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        setPushOn(!!sub);
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  const enablePush = useCallback(async ({ silent = false } = {}) => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      if (!silent) toast.error("This browser doesn't support notifications. Try Chrome (Android) or install this site to home screen on iPhone.");
      return false;
    }
    try {
      const perm = Notification.permission === "granted"
        ? "granted"
        : await Notification.requestPermission();
      if (perm !== "granted") {
        if (!silent) toast.error("Notifications blocked. Enable in browser settings.");
        return false;
      }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/public-key");
      const existing = await reg.pushManager.getSubscription();
      const sub = existing || (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      }));
      const json = sub.toJSON();
      await api.post("/owner/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      setPushOn(true);
      if (!silent) toast.success("Notifications on. You'll get a ping the moment a lead comes in.");
      return true;
    } catch (e) {
      console.error(e);
      if (!silent) toast.error("Couldn't enable notifications");
      return false;
    }
  }, []);

  // Auto-enable push after login — silently if already granted, otherwise
  // prompt exactly once per device (remembers via localStorage).
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return;
    const ASKED_KEY = "wpf_push_asked_v1";
    (async () => {
      try {
        const perm = Notification.permission;
        if (perm === "granted") {
          // Silently ensure this device is subscribed on the backend
          await enablePush({ silent: true });
          return;
        }
        if (perm === "denied") return; // user blocked it — never auto-nag
        // perm === "default" — only ask once per device
        if (localStorage.getItem(ASKED_KEY)) return;
        localStorage.setItem(ASKED_KEY, "1");
        // Give the dashboard a beat to render so the permission popup isn't jarring
        setTimeout(() => { enablePush({ silent: false }); }, 800);
      } catch (_e) { /* ignore */ }
    })();
  }, [enablePush]);


  const disablePush = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        const json = sub.toJSON();
        await api.post("/owner/push/unsubscribe", { endpoint: json.endpoint, keys: json.keys });
        await sub.unsubscribe();
      }
      setPushOn(false);
      toast.success("Notifications off.");
    } catch {
      toast.error("Couldn't turn off notifications");
    }
  };

  const testPush = async () => {
    try {
      const { data } = await api.post("/owner/push/test");
      toast.success(`Test sent to ${data.sent} device${data.sent === 1 ? "" : "s"}`);
    } catch {
      toast.error("Test push failed");
    }
  };

  const toggleHandled = async (id, handled) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, handled } : l)));
    try {
      await api.patch(`/owner/leads/${id}/handled?handled=${handled}`);
    } catch {
      toast.error("Couldn't update lead");
      load();
    }
  };

  const [selectedIds, setSelectedIds] = useState(new Set());
  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = () => setSelectedIds(new Set(filtered.map((l) => l.id)));

  const deleteLead = async (id, name = "lead") => {
    if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
    setLeads((ls) => ls.filter((l) => l.id !== id));
    setSelectedIds((s) => { const n = new Set(s); n.delete(id); return n; });
    try {
      await api.delete(`/owner/leads/${id}`);
      toast.success("Lead deleted");
    } catch {
      toast.error("Couldn't delete lead");
      load();
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} lead${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    setLeads((ls) => ls.filter((l) => !selectedIds.has(l.id)));
    clearSelection();
    try {
      const { data } = await api.post("/owner/leads/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} lead${data.deleted === 1 ? "" : "s"}`);
    } catch {
      toast.error("Couldn't delete leads");
      load();
    }
  };

  const deleteAll = async () => {
    if (!window.confirm("DELETE EVERY lead in the dashboard? This wipes both Catering and Contact submissions and can't be undone.")) return;
    if (!window.confirm("Really wipe all leads? Final chance to cancel.")) return;
    try {
      const { data } = await api.post("/owner/leads/delete-all");
      toast.success(`Wiped ${data.deleted} leads`);
      clearSelection();
      load();
    } catch {
      toast.error("Couldn't wipe leads");
    }
  };

  const filtered = leads.filter((l) => {
    if (filter === "unhandled") return !l.handled;
    if (filter === "catering") return l.kind === "catering";
    if (filter === "contact") return l.kind === "contact";
    if (filter === "holiday") return l.kind === "holiday";
    return true;
  });
  const unhandled = leads.filter((l) => !l.handled).length;

  // Tab-title badge + installed-PWA app-icon badge — always reflects unhandled count
  useEffect(() => {
    const base = baseTitleRef.current || "Owner · When Pigs Fly BBQ";
    document.title = unhandled > 0 ? `(${unhandled}) ${base}` : base;
    // App badge on installed PWA (Chrome desktop, Android, iOS 16.4+)
    try {
      if ("setAppBadge" in navigator) {
        if (unhandled > 0) navigator.setAppBadge(unhandled).catch(() => {});
        else navigator.clearAppBadge?.().catch(() => {});
      }
    } catch (_e) { /* ignore */ }
    return () => {
      document.title = base;
      try { navigator.clearAppBadge?.().catch(() => {}); } catch (_e) { /* ignore */ }
    };
  }, [unhandled]);

  return (
    <div>
      <section className="border-b border-ink bg-ink py-10 text-bone">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-4 px-4 md:flex-row md:items-center md:justify-between md:px-8">
          <div>
            <div className="eyebrow mb-1 text-bone/60">Owner Dashboard</div>
            <h1 className="font-rye text-4xl uppercase leading-none sm:text-5xl">{{ leads: "Leads", menu: "Menu", store: "Store", gallery: "Gallery", blog: "Blog" }[section] || "Leads"}</h1>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.24em] text-bone/60">
              Signed in as {owner.email} ·{" "}
              {unhandled > 0 ? (
                <span
                  data-testid="unhandled-count"
                  className="ml-1 inline-flex items-center gap-1 bg-red-600 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-bone"
                >
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-bone animate-pulse" /> {unhandled} unhandled
                </span>
              ) : (
                <span data-testid="unhandled-count" className="text-bone/60">0 unhandled</span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="refresh-btn"
              onClick={load}
              className="inline-flex items-center gap-2 border border-bone px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] transition-colors hover:bg-bone hover:text-ink"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            {pushOn ? (
              <>
                <button data-testid="push-test-btn" onClick={testPush} className="border border-bone px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] transition-colors hover:bg-bone hover:text-ink">
                  Test
                </button>
                <button data-testid="push-off-btn" onClick={disablePush} className="inline-flex items-center gap-2 border border-bone px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] transition-colors hover:bg-bone hover:text-ink">
                  <BellOff size={14} /> Notifications on
                </button>
              </>
            ) : (
              <button data-testid="push-on-btn" onClick={enablePush} className="inline-flex items-center gap-2 border-2 border-bone bg-bone px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-ink transition-colors hover:bg-transparent hover:text-bone">
                <Bell size={14} /> Turn on notifications
              </button>
            )}
            <button data-testid="logout-btn" onClick={onLogout} className="inline-flex items-center gap-2 border border-bone px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] transition-colors hover:bg-bone hover:text-ink">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </section>

      {/* Section switcher — Leads / Store / Gallery */}
      <section className="border-b border-ink bg-white py-4" data-testid="dashboard-section-tabs">
        <div className="mx-auto flex max-w-[1400px] flex-wrap gap-2 px-4 md:px-8">
          {[
            { k: "leads", label: `Leads (${leads.length})` },
            { k: "menu", label: "Menu Manager" },
            { k: "store", label: "Store Manager" },
            { k: "gallery", label: "Gallery Manager" },
            { k: "blog", label: "Blog Manager" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setSection(t.k)}
              data-testid={`section-tab-${t.k}`}
              className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.24em] ${section === t.k ? "border-ink bg-ink text-bone" : "border-ink/40 text-ink hover:bg-ink/5"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      {section === "menu" && (
        <section className="bg-bone py-10 md:py-16" data-testid="menu-manager-section">
          <div className="mx-auto max-w-[1400px] px-4 md:px-8">
            <AdminMenuTab />
          </div>
        </section>
      )}

      {section === "store" && (
        <section className="bg-bone py-10 md:py-16" data-testid="store-manager-section">
          <div className="mx-auto max-w-[1400px] px-4 md:px-8">
            <AdminStoreTab />
          </div>
        </section>
      )}

      {section === "gallery" && (
        <section className="bg-bone py-10 md:py-16" data-testid="gallery-manager-section">
          <div className="mx-auto max-w-[1400px] px-4 md:px-8">
            <AdminGalleryTab />
          </div>
        </section>
      )}

      {section === "blog" && (
        <section className="bg-bone py-10 md:py-16" data-testid="blog-manager-section">
          <div className="mx-auto max-w-[1400px] px-4 md:px-8">
            <AdminBlogTab />
          </div>
        </section>
      )}

      {section === "leads" && <>
      <section className="border-b border-ink bg-white py-6" data-testid="dashboard-filters">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3 px-4 md:px-8">
          <div className="flex flex-wrap gap-2">
            {[
              { k: "all", label: `All (${leads.length})` },
              { k: "unhandled", label: `Unhandled (${unhandled})` },
              { k: "holiday", label: `Holiday Orders (${leads.filter((l) => l.kind === "holiday").length})` },
              { k: "catering", label: `Catering (${leads.filter((l) => l.kind === "catering").length})` },
              { k: "contact", label: `Contact (${leads.filter((l) => l.kind === "contact").length})` },
            ].map((f) => (
              <button
                key={f.k}
                data-testid={`filter-${f.k}`}
                onClick={() => setFilter(f.k)}
                className={`border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] ${filter === f.k ? "border-ink bg-ink text-bone" : "border-ink/30 text-ink hover:bg-ink/5"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              data-testid="select-all-visible"
              onClick={selectAllVisible}
              className="border border-ink/30 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-ink hover:bg-ink/5"
            >
              Select visible
            </button>
            {selectedIds.size > 0 && (
              <>
                <span data-testid="selected-count" className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink/60">
                  {selectedIds.size} selected
                </span>
                <button
                  data-testid="delete-selected"
                  onClick={deleteSelected}
                  className="inline-flex items-center gap-2 border-2 border-red-700 bg-red-700 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-bone hover:bg-transparent hover:text-red-700"
                >
                  <Trash2 size={14} /> Delete selected
                </button>
                <button
                  onClick={clearSelection}
                  className="border border-ink/30 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-ink hover:bg-ink/5"
                >
                  Clear
                </button>
              </>
            )}
            <button
              data-testid="delete-all-leads"
              onClick={deleteAll}
              className="ml-2 inline-flex items-center gap-2 border border-red-700/40 bg-transparent px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-red-700 hover:bg-red-700 hover:text-bone"
            >
              <Trash2 size={14} /> Wipe all
            </button>
          </div>
        </div>
      </section>

      <section className="bg-bone py-10 md:py-16">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          {loading ? (
            <div className="font-mono text-xs uppercase tracking-[0.24em] text-ink/60">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-ink p-10 text-center font-serif text-ink/70">
              No leads in this view. When someone submits a catering, contact, event or holiday order form, it lands here.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
              {filtered.map((l) => (
                <LeadCard
                  key={l.id}
                  lead={l}
                  onToggle={toggleHandled}
                  onDelete={deleteLead}
                  selected={selectedIds.has(l.id)}
                  onSelectToggle={toggleSelect}
                  isNew={newIds.has(l.id)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      </>}
    </div>
  );
}




export default function Owner() {
  usePageMeta({ title: "Owner", description: "Owner-only dashboard for When Pigs Fly BBQ.", path: "/owner" });
  const [owner, setOwner] = useState(undefined); // undefined = loading, null = logged out
  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setOwner(data);
    } catch {
      setOwner(null);
    }
  }, []);
  useEffect(() => { check(); }, [check]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    setOwner(null);
  };

  if (owner === undefined) {
    return <div className="mx-auto max-w-md px-4 py-24 font-mono text-xs uppercase tracking-[0.24em] text-ink/60">Loading…</div>;
  }
  if (owner === null) return <LoginForm onLoggedIn={check} />;
  return <Dashboard owner={owner} onLogout={logout} />;
}
